/**
 * Tide Run rules (docs/TIDE_RUN.md): the sea rises below the climb, matched to
 * the player's own typing speed, and a run chains level after level until the
 * water catches them.
 *
 * Pure: no Phaser. Game renders the water and asks this module where it is.
 */
import { THEME_BANDS } from '../data/themes';
import { typingSeconds } from './LevelGenerator';
import type { RunEntry } from './SaveManager';
import type { AnimalId } from '../data/animals';

/** Tide Run opens once this level is complete in normal play. */
export const TIDE_UNLOCK_LEVEL = 5;

/** The pace is set from this many recent runs. */
export const PACE_HISTORY_RUNS = 10;
/** A stretch: a little faster than the player's own average. */
export const PACE_FACTOR = 1.1;
/** Brand-new players, and anyone whose history is very slow. */
export const PACE_FLOOR_WPM = 6;
/** Never absurd, however fast the history says the player is. */
export const PACE_CAP_WPM = 45;
/** Each level cleared in the same run raises the pace by this much. */
export const LEVEL_SPEEDUP = 1.04;

/** The water starts this many platforms below the starting floe. */
export const WATER_START_BELOW = 2;
/** Seconds before the water starts to move on each level. */
export const GRACE_SECONDS = 3;
/** Within this many platforms of the animal, the warning shows. */
export const NEAR_WATER_MARGIN = 1.5;

/** Tide stars: 2 for clearing at about this speed, 3 at THREE_STAR_WPM. */
export const TWO_STAR_WPM = 15;
export const THREE_STAR_WPM = 25;

/* -------------------------------------------------------------------- pace */

/**
 * The run's starting pace: the average WPM of the last ten runs, x1.1,
 * between the floor and the cap. Fixed for the whole run, so it can't be
 * gamed by slowing down part way.
 */
export function paceFromHistory(history: readonly RunEntry[]): number {
  const recent = history.slice(-PACE_HISTORY_RUNS).filter((run) => run.wpm > 0);
  if (recent.length === 0) return PACE_FLOOR_WPM;
  const average = recent.reduce((sum, run) => sum + run.wpm, 0) / recent.length;
  return Math.min(PACE_CAP_WPM, Math.max(PACE_FLOOR_WPM, average * PACE_FACTOR));
}

/** The pace for a level, after `levelsCleared` levels in this run. */
export function paceForLevel(baseWpm: number, levelsCleared: number): number {
  return baseWpm * Math.pow(LEVEL_SPEEDUP, Math.max(0, levelsCleared));
}

/** Seconds the water takes to rise one platform: typing that word at pace. */
export function secondsPerWord(word: string, wpm: number): number {
  return typingSeconds(Math.max(1, word.length), Math.max(1, wpm));
}

/* ------------------------------------------------------------------- water */

export interface WaterState {
  /** Surface height in platform units: 0 is level with the starting floe. */
  readonly position: number;
  readonly graceLeft: number;
}

export function startWater(): WaterState {
  return { position: -WATER_START_BELOW, graceLeft: GRACE_SECONDS };
}

/**
 * Advance the water by `dtSec`.
 *
 * `secondsFor(k)` is how long the rise to platform k takes. Each platform is
 * crossed at its own speed, so a long word gives more time, and a step that
 * crosses a platform boundary is split at it.
 */
export function stepWater(
  water: WaterState,
  dtSec: number,
  secondsFor: (platform: number) => number,
): WaterState {
  let dt = Math.max(0, dtSec);
  let graceLeft = water.graceLeft;
  if (graceLeft > 0) {
    const used = Math.min(graceLeft, dt);
    graceLeft -= used;
    dt -= used;
  }
  let position = water.position;
  // Guards a zero-length word table: never loops forever.
  for (let guard = 0; dt > 0 && guard < 1000; guard += 1) {
    const next = Math.floor(position) + 1;
    const seconds = Math.max(0.05, secondsFor(next));
    const toNext = (next - position) * seconds;
    if (dt < toNext) {
      position += dt / seconds;
      dt = 0;
    } else {
      position = next;
      dt -= toNext;
    }
  }
  return { position, graceLeft };
}

/** The word table as a `secondsFor`: below the start, the first word's time. */
export function waterTiming(
  words: readonly (string | undefined)[],
  wpm: number,
): (platform: number) => number {
  const first = words.find((word) => word !== undefined) ?? 'word';
  return (platform) => secondsPerWord(words[platform] ?? first, wpm);
}

/** Platforms of clear air between the water and the animal's feet. */
export function waterMargin(water: WaterState, platformIndex: number): number {
  return platformIndex - water.position;
}

export function isCaught(water: WaterState, platformIndex: number): boolean {
  return waterMargin(water, platformIndex) <= 0;
}

export function isNearWater(water: WaterState, platformIndex: number): boolean {
  return waterMargin(water, platformIndex) < NEAR_WATER_MARGIN;
}

/* ------------------------------------------------------------ tide stars */

/** Seconds to type every word of a level at `wpm`, as a fixed par. */
export function parSeconds(words: readonly (string | undefined)[], wpm: number): number {
  return words.reduce(
    (sum, word) => sum + (word === undefined ? 0 : typingSeconds(word.length, wpm)),
    0,
  );
}

/** 1 for clearing the level, 2 at about 15 WPM, 3 at about 25. */
export function tideStarsFor(clearSec: number, words: readonly (string | undefined)[]): number {
  if (clearSec <= parSeconds(words, THREE_STAR_WPM)) return 3;
  if (clearSec <= parSeconds(words, TWO_STAR_WPM)) return 2;
  return 1;
}

/* ------------------------------------------------------------------ worlds */

export function isTideUnlocked(isLevelComplete: (level: number) => boolean): boolean {
  return isLevelComplete(TIDE_UNLOCK_LEVEL);
}

/** A run starts at the first level of any world the player has reached. */
export function tideStartLevels(isLevelUnlocked: (level: number) => boolean): number[] {
  return THEME_BANDS.map((band) => band.fromLevel).filter((level) => isLevelUnlocked(level));
}

/* --------------------------------------------------------------- cosmetics */

export type CosmeticId = 'swatch-ocean' | 'swatch-coral-reef' | 'swatch-gold' | 'swatch-aurora';

export interface TideProgress {
  readonly mostLevelsInRun: number;
  readonly tideStars: number;
}

export interface CosmeticMilestone {
  readonly id: CosmeticId;
  readonly name: string;
  /** "Clear 3 levels in one Tide Run", for the locked swatch. */
  readonly hint: string;
  /** How far along the player is, and how far they need to get. */
  readonly have: (progress: TideProgress) => number;
  readonly need: number;
}

export const COSMETIC_MILESTONES: readonly CosmeticMilestone[] = [
  {
    id: 'swatch-ocean',
    name: 'Ocean',
    hint: 'Clear 3 levels in one Tide Run',
    have: (p) => p.mostLevelsInRun,
    need: 3,
  },
  {
    id: 'swatch-coral-reef',
    name: 'Coral Reef',
    hint: 'Earn 15 tide stars (your best on each level counts)',
    have: (p) => p.tideStars,
    need: 15,
  },
  {
    id: 'swatch-gold',
    name: 'Gold',
    hint: 'Clear 10 levels in one Tide Run',
    have: (p) => p.mostLevelsInRun,
    need: 10,
  },
  {
    id: 'swatch-aurora',
    name: 'Aurora',
    hint: 'Earn 50 tide stars (your best on each level counts)',
    have: (p) => p.tideStars,
    need: 50,
  },
];

export function earnedCosmetics(progress: TideProgress): CosmeticId[] {
  return COSMETIC_MILESTONES.filter((m) => m.have(progress) >= m.need).map((m) => m.id);
}

export function isCosmeticId(value: string): value is CosmeticId {
  return COSMETIC_MILESTONES.some((m) => m.id === value);
}

/* --------------------------------------------------------------------- run */

/** One level cleared during a run. */
export interface TideLevelClear {
  readonly level: number;
  readonly timeSec: number;
  readonly stars: number;
  /** This clear beat the level's previous best tide time (or set the first). */
  readonly newBest: boolean;
}

/** Everything a run carries from one level to the next. */
export interface TideRun {
  readonly startLevel: number;
  readonly baseWpm: number;
  readonly clears: readonly TideLevelClear[];
  readonly elapsedMs: number;
  readonly correctChars: number;
  readonly wordsCorrect: number;
  readonly wordsAttempted: number;
  readonly keystrokes: number;
  readonly keystrokeHits: number;
  readonly missedWords: readonly string[];
  readonly missedLetters: Readonly<Record<string, number>>;
  /** Rewards granted during the run, for the summary to announce. */
  readonly newCosmetics: readonly CosmeticId[];
  readonly newAnimals: readonly AnimalId[];
}

export function startTideRun(startLevel: number, history: readonly RunEntry[]): TideRun {
  return {
    startLevel,
    baseWpm: paceFromHistory(history),
    clears: [],
    elapsedMs: 0,
    correctChars: 0,
    wordsCorrect: 0,
    wordsAttempted: 0,
    keystrokes: 0,
    keystrokeHits: 0,
    missedWords: [],
    missedLetters: {},
    newCosmetics: [],
    newAnimals: [],
  };
}

/** One level's typing, as Game counts it, folded into the run. */
export interface TideLevelTyping {
  readonly elapsedMs: number;
  readonly correctChars: number;
  readonly wordsCorrect: number;
  readonly wordsAttempted: number;
  readonly keystrokes: number;
  readonly keystrokeHits: number;
  readonly missedWords: readonly string[];
  readonly missedLetters: Readonly<Record<string, number>>;
}

export function addLevelToRun(
  run: TideRun,
  typing: TideLevelTyping,
  clear: TideLevelClear | null,
): TideRun {
  const missedLetters: Record<string, number> = { ...run.missedLetters };
  for (const [key, count] of Object.entries(typing.missedLetters)) {
    missedLetters[key] = (missedLetters[key] ?? 0) + count;
  }
  return {
    ...run,
    clears: clear === null ? run.clears : [...run.clears, clear],
    elapsedMs: run.elapsedMs + typing.elapsedMs,
    correctChars: run.correctChars + typing.correctChars,
    wordsCorrect: run.wordsCorrect + typing.wordsCorrect,
    wordsAttempted: run.wordsAttempted + typing.wordsAttempted,
    keystrokes: run.keystrokes + typing.keystrokes,
    keystrokeHits: run.keystrokeHits + typing.keystrokeHits,
    missedWords: [...run.missedWords, ...typing.missedWords],
    missedLetters,
  };
}

/** How the run ended: caught by the water, or off the top of level 100. */
/** 'stopped': left through the pause menu. It still counts, with no summary. */
export type TideOutcome = 'caught' | 'summit' | 'stopped';

export interface TideRunSummary {
  readonly outcome: TideOutcome;
  readonly startLevel: number;
  /** The level the run ended on (or the last one, for a summit). */
  readonly reachedLevel: number;
  readonly levelsCleared: number;
  readonly elapsedMs: number;
  readonly wpm: number;
  readonly accuracy: number;
  readonly tideStarsEarned: number;
  readonly clears: readonly TideLevelClear[];
}

export function summarizeRun(run: TideRun, outcome: TideOutcome, endLevel: number): TideRunSummary {
  const minutes = run.elapsedMs / 60000;
  return {
    outcome,
    startLevel: run.startLevel,
    reachedLevel: endLevel,
    levelsCleared: run.clears.length,
    elapsedMs: run.elapsedMs,
    wpm: minutes > 0 ? run.correctChars / 5 / minutes : 0,
    accuracy: run.keystrokes === 0 ? 100 : (run.keystrokeHits / run.keystrokes) * 100,
    tideStarsEarned: run.clears.reduce((sum, clear) => sum + clear.stars, 0),
    clears: run.clears,
  };
}

/** "1:05" for a run clock. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
